"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { useWordList } from "@/components/tools/word-games/use-word-list";
import { WordListStatus } from "@/components/tools/word-games/word-list-status";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  changedPosition,
  type LadderResult,
  MAX_LENGTH,
  MIN_LENGTH,
  parseExcluded,
  parseLadderInput,
  solveLadder,
} from "@/lib/tool-kit/word-games/word-ladder";

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_LADDER_SOLVER_STRINGS = {
  startLabel: "Start word",
  startPlaceholder: "cold",
  endLabel: "End word",
  endPlaceholder: "warm",
  excludeLabel: "Words to avoid (optional)",
  excludeHint:
    "Separate with spaces or commas. Handy for skipping obscure words the list contains.",
  swap: "Swap the words",
  tryLabel: "Try:",
  exampleStarts: ["cold", "cat", "love"],
  exampleEnds: ["warm", "dog", "hate"],
  emptyPrompt:
    "Enter a start word and an end word of the same length to find the shortest word ladder between them.",
  invalid: "Use letters only.",
  lengthMismatch: "The two words must have the same number of letters.",
  range: "Use words of {min} to {max} letters.",
  sameWord:
    "The start and end words are the same, so no letters need to change.",
  loading: "Loading the word list (once per visit)…",
  loadError:
    "Could not load the word list. Check your connection and try again.",
  retry: "Try again",
  none: "No ladder joins these words using the word list and the words you avoid. Changing one letter at a time from the start word, only {reached} can be reached.",
  reachedWords: "one:{count} word|other:{count} words",
  heading: "Shortest ladder",
  statSteps: "Letter changes",
  statWords: "Words in the ladder",
  statCount: "Shortest ladders",
  stepLabel: "Step {step}",
  startTag: "start",
  endTag: "end",
  unlisted:
    "{words} is not in the word list, so it is used only as an end of the ladder.",
  othersHeading: "Other shortest ladders",
  othersCount: "Showing {shown} of {count}.",
  copyLadder: "Copy ladder",
  copied: "Copied!",
  sourceNote:
    "Steps use the ENABLE word list, which includes many rare words and no proper nouns. If a ladder uses a word you do not want, add it to the words to avoid.",
} as const;

type Strings = typeof WORD_LADDER_SOLVER_STRINGS;
type Found = Extract<LadderResult, { kind: "found" }>;

const LIMIT = 12;
const RE_LETTERS_ONLY = /[^a-z]/gi;
const INPUT_KEYS = {
  invalid: "invalid",
  length: "lengthMismatch",
  range: "range",
} as const;

/** One word of a ladder, with the letter that changed since the last word marked. */
function LadderWord({
  word,
  previous,
}: {
  word: string;
  previous: string | undefined;
}) {
  const changed = previous === undefined ? -1 : changedPosition(previous, word);
  return (
    <span className="font-mono text-foreground text-lg uppercase tracking-wider">
      {Array.from(word).map((letter, index) => (
        <span
          className={
            index === changed
              ? "rounded bg-primary/20 px-0.5 font-bold text-primary"
              : undefined
          }
          // biome-ignore lint/suspicious/noArrayIndexKey: letters repeat; position is the identity
          key={index}
        >
          {letter}
        </span>
      ))}
    </span>
  );
}

function MainLadder({
  ladder,
  strings,
}: {
  ladder: readonly string[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  return (
    <ol className="space-y-1.5">
      {ladder.map((word, index) => {
        let tag: string | null = null;
        if (index === 0) {
          tag = strings.startTag;
        } else if (index === ladder.length - 1) {
          tag = strings.endTag;
        }
        return (
          <li
            className="flex items-baseline gap-3 rounded-md border border-border bg-card px-3 py-1.5"
            key={`${index}-${word}`}
          >
            <span className={`${HINT} w-14 shrink-0`}>
              {t(strings.stepLabel, { step: fmt(index) })}
            </span>
            <LadderWord previous={ladder[index - 1]} word={word} />
            {tag ? <span className={HINT}>{tag}</span> : null}
          </li>
        );
      })}
    </ol>
  );
}

function Results({ found, strings }: { found: Found; strings: Strings }) {
  const fmt = useToolNumber();
  const [main, ...others] = found.ladders;
  return (
    <div aria-live="polite" className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className={WIDGET_H2}>{strings.heading}</h2>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyLadder}
          small
          text={main.join(" → ")}
        />
      </div>
      <StatGrid>
        <StatCard emphasis label={strings.statSteps} value={fmt(found.steps)} />
        <StatCard label={strings.statWords} value={fmt(main.length)} />
        <StatCard label={strings.statCount} value={fmt(found.count)} />
      </StatGrid>
      {found.unlisted.length > 0 ? (
        <p className={HINT}>
          {t(strings.unlisted, { words: found.unlisted.join(", ") })}
        </p>
      ) : null}
      <MainLadder ladder={main} strings={strings} />
      {others.length > 0 ? (
        <section className="space-y-2">
          <h3 className={WIDGET_H3}>{strings.othersHeading}</h3>
          <p className={HINT}>
            {t(strings.othersCount, {
              shown: fmt(found.ladders.length),
              count: fmt(found.count),
            })}
          </p>
          <ul className="space-y-1.5">
            {others.map((ladder) => (
              <li
                className="font-mono text-foreground text-sm"
                key={ladder.join(">")}
              >
                {ladder.join(" → ")}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export default function WordLadderSolverWidget() {
  const strings = useToolUi(WORD_LADDER_SOLVER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [exclude, setExclude] = useState("");
  const parsed = parseLadderInput(start, end);
  const same = parsed.ok && parsed.start === parsed.end;
  const { words, status, retry } = useWordList(parsed.ok && !same);
  const result = useMemo(() => {
    const input = parseLadderInput(start, end);
    if (!(input.ok && words) || input.start === input.end) {
      return null;
    }
    return solveLadder(
      words,
      input.start,
      input.end,
      parseExcluded(exclude),
      LIMIT
    );
  }, [start, end, exclude, words]);

  function notice() {
    if (parsed.ok) {
      return same ? <Notice>{strings.sameWord}</Notice> : null;
    }
    if (parsed.reason === "empty") {
      return <Notice>{strings.emptyPrompt}</Notice>;
    }
    return (
      <ErrorNotice>
        {t(strings[INPUT_KEYS[parsed.reason]], {
          min: fmt(MIN_LENGTH),
          max: fmt(MAX_LENGTH),
        })}
      </ErrorNotice>
    );
  }

  return (
    <>
      <div className="grid items-end gap-4 sm:grid-cols-[1fr_1fr_auto]">
        <TextField
          id="ladder-start"
          label={strings.startLabel}
          mono
          onChange={(value) =>
            setStart(
              value.replace(RE_LETTERS_ONLY, "").slice(0, MAX_LENGTH + 2)
            )
          }
          placeholder={strings.startPlaceholder}
          value={start}
        />
        <TextField
          id="ladder-end"
          label={strings.endLabel}
          mono
          onChange={(value) =>
            setEnd(value.replace(RE_LETTERS_ONLY, "").slice(0, MAX_LENGTH + 2))
          }
          placeholder={strings.endPlaceholder}
          value={end}
        />
        <button
          className={BUTTON_SECONDARY}
          disabled={!(start || end)}
          onClick={() => {
            setStart(end);
            setEnd(start);
          }}
          type="button"
        >
          {strings.swap}
        </button>
      </div>
      <TextField
        hint={strings.excludeHint}
        id="ladder-exclude"
        label={strings.excludeLabel}
        mono
        onChange={(value) => setExclude(value.slice(0, 200))}
        value={exclude}
      />
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className={HINT}>{strings.tryLabel}</span>
        {strings.exampleStarts.map((first, index) => (
          <button
            className="font-medium font-mono text-primary underline-offset-4 hover:underline"
            key={first}
            onClick={() => {
              setStart(first);
              setEnd(strings.exampleEnds[index]);
            }}
            type="button"
          >
            {first} → {strings.exampleEnds[index]}
          </button>
        ))}
      </p>
      {notice()}
      <WordListStatus
        error={strings.loadError}
        loading={strings.loading}
        onRetry={retry}
        retry={strings.retry}
        status={status}
      />
      {result?.kind === "none" ? (
        <Notice>
          {t(strings.none, {
            reached: tPlural(strings.reachedWords, locale, result.reached, {
              count: fmt(result.reached),
            }),
          })}
        </Notice>
      ) : null}
      {result?.kind === "found" ? (
        <Results found={result} strings={strings} />
      ) : null}
      <p className={HINT}>{strings.sourceNote}</p>
    </>
  );
}
